import { useState } from 'react';
import { Link } from 'react-router-dom';

interface QuizQuestion {
  id: string;
  question: string;
  options: { label: string; value: string; icon: string }[];
}

const QUESTIONS: QuizQuestion[] = [
  {
    id: 'age',
    question: 'How old are you?',
    options: [
      { label: '50–59', value: '50s', icon: 'ri-user-line' },
      { label: '60–69', value: '60s', icon: 'ri-user-3-line' },
      { label: '70–79', value: '70s', icon: 'ri-user-heart-line' },
      { label: '80–85', value: '80s', icon: 'ri-heart-line' },
    ],
  },
  {
    id: 'health',
    question: 'How would you describe your overall health?',
    options: [
      { label: 'Excellent — no major conditions', value: 'excellent', icon: 'ri-heart-pulse-line' },
      { label: 'Good — minor controlled conditions', value: 'good', icon: 'ri-heart-2-line' },
      { label: 'Fair — multiple conditions', value: 'fair', icon: 'ri-medicine-bottle-line' },
      { label: 'Poor — serious health issues', value: 'poor', icon: 'ri-hospital-line' },
    ],
  },
  {
    id: 'goal',
    question: 'What is your #1 goal for this coverage?',
    options: [
      { label: 'Cover funeral & burial costs', value: 'final_expense', icon: 'ri-service-line' },
      { label: 'Protect income for my spouse', value: 'income', icon: 'ri-couple-line' },
      { label: 'Leave money for my children', value: 'legacy', icon: 'ri-gift-line' },
      { label: 'Pay off remaining debts', value: 'debt', icon: 'ri-bank-card-line' },
    ],
  },
  {
    id: 'exam',
    question: 'Are you comfortable with a brief health questionnaire?',
    options: [
      { label: 'Yes — happy to answer a few questions', value: 'yes', icon: 'ri-file-list-3-line' },
      { label: 'No — I want guaranteed acceptance, no questions', value: 'no', icon: 'ri-shield-check-line' },
    ],
  },
];

interface Recommendation {
  plan: string;
  subtitle: string;
  description: string;
  bullets: string[];
  badge: string;
  badgeColor: string;
  cta: string;
  href: string;
  icon: string;
}

function getRecommendation(answers: Record<string, string>): Recommendation {
  const { age, health, goal, exam } = answers;

  // Guaranteed issue — no exam, serious health, or 80+
  if (exam === 'no' || health === 'poor' || age === '80s') {
    return {
      plan: 'Guaranteed Issue Life Insurance',
      subtitle: 'No health questions — guaranteed acceptance',
      description:
        'Because you\'d prefer no health questions (or have serious health conditions), Guaranteed Issue is the perfect fit. You literally cannot be denied — 100% of applicants ages 50–85 are accepted instantly.',
      bullets: [
        'Zero health questions — ever',
        'Instant approval, same day',
        'Coverage: $5,000–$25,000',
        'Premiums never increase',
        'Perfect for final expense & burial',
      ],
      badge: 'Best Match',
      badgeColor: 'bg-[#1A3A52] text-white',
      cta: 'Get Guaranteed Issue Quote',
      href: '/final-expense',
      icon: 'ri-shield-check-fill',
    };
  }

  // Final expense / burial — smaller coverage, simple
  if (goal === 'final_expense' || (age === '70s' && health !== 'excellent')) {
    return {
      plan: 'Final Expense Insurance',
      subtitle: 'Simplified issue — no medical exam required',
      description:
        'For covering funeral, burial, and end-of-life costs, Final Expense Insurance is the #1 choice for Indiana seniors. Just answer a short health questionnaire — no exam, no blood draw — and most applicants are approved in 24–48 hours.',
      bullets: [
        'No medical exam required',
        'Coverage: $5,000–$35,000',
        'Approval: 24–48 hours',
        'Covers funeral, burial, medical bills',
        'Premiums are locked in for life',
      ],
      badge: 'Best Match',
      badgeColor: 'bg-[#D97642] text-white',
      cta: 'Get Final Expense Quote',
      href: '/final-expense',
      icon: 'ri-hand-heart-fill',
    };
  }

  // Income replacement / legacy — term or whole life
  if (goal === 'income' || goal === 'legacy' || goal === 'debt') {
    if (age === '50s' || (age === '60s' && health === 'excellent')) {
      return {
        plan: 'Term Life Insurance',
        subtitle: 'Simplified underwriting — fast approval, high coverage',
        description:
          'With your goal of income protection or leaving a legacy, a Term Life policy gives you the most coverage for the lowest premium. Many Indiana seniors in their 50s and early 60s qualify for $100K–$500K+ with no exam.',
        bullets: [
          'High coverage amounts (up to $500K+)',
          'No exam — simplified underwriting',
          'Approval: same day to 48 hours',
          'Covers income, mortgage, legacy',
          '10 or 20-year terms available',
        ],
        badge: 'Best Match',
        badgeColor: 'bg-[#D97642] text-white',
        cta: 'Get Term Life Quote',
        href: '/term-life',
        icon: 'ri-shield-check-fill',
      };
    }
    return {
      plan: 'Whole Life Insurance',
      subtitle: 'Permanent simplified issue — cash value that lasts forever',
      description:
        'For a permanent legacy or income protection, Simplified Issue Whole Life gives you lifetime coverage that never expires plus tax-deferred cash value growth — with no medical exam required.',
      bullets: [
        'Permanent — never expires',
        'Coverage: $25,000–$100,000',
        'No medical exam required',
        'Builds cash value over time',
        'Tax-free death benefit',
      ],
      badge: 'Best Match',
      badgeColor: 'bg-[#1A3A52] text-white',
      cta: 'Get Whole Life Quote',
      href: '/whole-life',
      icon: 'ri-infinity-line',
    };
  }

  // Default: final expense
  return {
    plan: 'Final Expense Insurance',
    subtitle: 'Most popular for Indiana seniors',
    description:
      'Based on your profile, Final Expense Insurance is your best fit. Easy approval with just a few health questions — and most Indiana seniors qualify for immediate full coverage starting day one.',
    bullets: [
      'No medical exam required',
      'Coverage: $5,000–$35,000',
      'Approval: 24–48 hours',
      'Premiums never increase',
      'Immediate full coverage (day 1)',
    ],
    badge: 'Best Match',
    badgeColor: 'bg-[#D97642] text-white',
    cta: 'Get Final Expense Quote',
    href: '/final-expense',
    icon: 'ri-hand-heart-fill',
  };
}

export default function SeniorPlanQuiz() {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [completed, setCompleted] = useState(false);

  const question = QUESTIONS[currentStep];
  const progress = Math.round((currentStep / QUESTIONS.length) * 100);

  function handleSelect(value: string) {
    const newAnswers = { ...answers, [question.id]: value };
    setAnswers(newAnswers);

    if (currentStep < QUESTIONS.length - 1) {
      setTimeout(() => setCurrentStep(s => s + 1), 200);
    } else {
      setTimeout(() => setCompleted(true), 200);
    }
  }

  function handleBack() {
    if (currentStep > 0) {
      setCurrentStep(s => s - 1);
    }
  }

  function handleRestart() {
    setCurrentStep(0);
    setAnswers({});
    setCompleted(false);
  }

  const recommendation = completed ? getRecommendation(answers) : null;

  return (
    <section className="py-16 bg-gradient-to-br from-[#FDF6F0] to-white">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-2 bg-[#D97642]/10 text-[#D97642] text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
            <i className="ri-questionnaire-line"></i>
            2-Minute Coverage Quiz
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-2">
            Which Plan Is Right for Me?
          </h2>
          <p className="text-gray-600 text-sm">
            Answer 4 quick questions and we'll match you to the best no-exam coverage option.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {!completed ? (
            <>
              {/* Progress bar */}
              <div className="h-1.5 bg-gray-100">
                <div
                  className="h-full bg-[#D97642] transition-all duration-500"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>

              <div className="p-8">
                {/* Step indicator */}
                <div className="flex items-center justify-between mb-6">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Question {currentStep + 1} of {QUESTIONS.length}
                  </span>
                  {currentStep > 0 && (
                    <button
                      onClick={handleBack}
                      className="text-xs text-gray-400 hover:text-[#1A3A52] flex items-center gap-1 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-line"></i> Back
                    </button>
                  )}
                </div>

                {/* Question */}
                <h3 className="text-xl font-bold text-[#1A3A52] mb-6 leading-snug">
                  {question.question}
                </h3>

                {/* Options */}
                <div className="grid grid-cols-1 gap-3">
                  {question.options.map(opt => {
                    const isSelected = answers[question.id] === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => handleSelect(opt.value)}
                        className={`flex items-center gap-4 px-5 py-4 rounded-xl border-2 text-left transition-all cursor-pointer whitespace-nowrap ${
                          isSelected
                            ? 'border-[#D97642] bg-[#D97642]/5'
                            : 'border-gray-200 hover:border-[#D97642]/50 bg-white hover:bg-[#FDF6F0]'
                        }`}
                      >
                        <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${isSelected ? 'bg-[#D97642] text-white' : 'bg-[#1A3A52]/5 text-[#1A3A52]'}`}>
                          <i className={`${opt.icon} text-lg`}></i>
                        </div>
                        <span className="font-semibold text-gray-900 text-sm">{opt.label}</span>
                        {isSelected && (
                          <i className="ri-check-line text-[#D97642] ml-auto text-lg"></i>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            /* Result */
            recommendation && (
              <div className="p-8">
                {/* Match badge */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#D97642]/10 rounded-xl">
                    <i className={`${recommendation.icon} text-[#D97642] text-2xl`}></i>
                  </div>
                  <div>
                    <span className={`inline-block px-3 py-1 text-xs font-bold rounded-full mb-1 ${recommendation.badgeColor}`}>
                      {recommendation.badge}
                    </span>
                    <div className="text-xs text-gray-500">{recommendation.subtitle}</div>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-[#1A3A52] mb-3">
                  {recommendation.plan}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">
                  {recommendation.description}
                </p>

                {/* Bullets */}
                <ul className="space-y-2 mb-6">
                  {recommendation.bullets.map((b, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-gray-700">
                      <i className="ri-check-line text-[#D97642] text-base flex-shrink-0"></i>
                      {b}
                    </li>
                  ))}
                </ul>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to={recommendation.href}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-colors whitespace-nowrap"
                  >
                    <i className="ri-arrow-right-line"></i>
                    {recommendation.cta}
                  </Link>
                  <Link
                    to="/book-appointment"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#1A3A52] text-white font-bold rounded-lg hover:bg-[#0F2A3F] transition-colors whitespace-nowrap"
                  >
                    <i className="ri-calendar-line"></i>
                    Talk to an Agent
                  </Link>
                </div>

                {/* Restart */}
                <button
                  onClick={handleRestart}
                  className="mt-4 w-full text-center text-xs text-gray-400 hover:text-[#D97642] transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line mr-1"></i> Retake the quiz
                </button>

                {/* Trust signals */}
                <div className="mt-5 pt-5 border-t border-gray-100 flex flex-wrap gap-3 justify-center">
                  {['Ages 50–85 qualify', 'No exam required', 'Cannot be denied (GI option)', 'Licensed Indiana agents'].map(t => (
                    <span key={t} className="flex items-center gap-1 text-xs text-gray-500">
                      <i className="ri-shield-check-line text-[#D97642]"></i>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )
          )}
        </div>

        {/* Footer note */}
        {!completed && (
          <p className="text-center text-xs text-gray-400 mt-4">
            No personal info required · Takes under 2 minutes · Free, no obligation
          </p>
        )}
      </div>
    </section>
  );
}
